/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Search, Globe, Briefcase, ChevronDown, ChevronUp, 
  Check, RefreshCw, X, AlertCircle, ShieldAlert, ShieldCheck, 
  Mail, Clock, Table, LayoutGrid, CheckCircle2, Eye, Filter, 
  Copy, MapPin, Building2, User, Calendar, FileText, Sparkles, 
  Layers, Database, ArrowUpRight, Trash2, ExternalLink
} from 'lucide-react';
import { Opportunity, Application, UserProfile } from '../../types';
import { StudentAvatar } from '../Common/StudentAvatar';
import { formatLastNameFirst } from '../../utils/studentUtils';
import { useInAppDialog } from '../Common/InAppDialog';

export const OpportunitiesAppsTabContent: React.FC = () => {
  const { 
    opportunities, 
    applications, 
    users, 
    tenants, 
    deleteOpportunity,
    activeUser 
  } = useWBL();

  const { showAlert, showConfirm, showToast } = useInAppDialog();

  // Active subtab view: Opportunities or Applications
  const [activeSubTab, setActiveSubTab] = useState<'opportunities' | 'applications'>('opportunities');

  // Search and regional filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clusterFilter, setClusterFilter] = useState('all');

  // Popout modal state
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // One-click copy Firestore ID helper
  const handleCopyId = (id: string, label: string = 'ID') => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    showToast({
      title: 'Copied to Clipboard',
      message: `${label} (${id}) copied to clipboard.`,
      type: 'success'
    });
    setTimeout(() => setCopiedId(null), 2500);
  };

  // Helper date formatter
  const formatDate = (dateVal?: any) => {
    if (!dateVal) return 'N/A';
    try {
      const d = typeof dateVal === 'string' ? new Date(dateVal) : new Date(dateVal.seconds ? dateVal.seconds * 1000 : dateVal);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Unique lists for filter dropdowns
  const uniqueClusters = useMemo(() => {
    const set = new Set<string>();
    opportunities.forEach(o => {
      if (o.careerCluster) set.add(o.careerCluster);
      if (o.industry) set.add(o.industry);
    });
    return Array.from(set).sort();
  }, [opportunities]);

  // Filtered Opportunities List
  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp => {
      // Search query filter (matches ID, title, organization, description, location)
      const q = searchQuery.toLowerCase().trim();
      const matchesQuery = !q || (
        (opp.id || '').toLowerCase().includes(q) ||
        (opp.title || '').toLowerCase().includes(q) ||
        (opp.organizationName || '').toLowerCase().includes(q) ||
        (opp.description || '').toLowerCase().includes(q) ||
        (opp.location || '').toLowerCase().includes(q) ||
        (opp.careerCluster || '').toLowerCase().includes(q) ||
        (opp.tenantId || '').toLowerCase().includes(q)
      );

      // Region filter
      const matchesRegion = regionFilter === 'all' || (opp.tenantId || '').toLowerCase() === regionFilter.toLowerCase();

      // Status filter
      const matchesStatus = statusFilter === 'all' || (opp.status || 'open').toLowerCase() === statusFilter.toLowerCase();

      // Cluster filter
      const matchesCluster = clusterFilter === 'all' || 
        (opp.careerCluster || '').toLowerCase() === clusterFilter.toLowerCase() ||
        (opp.industry || '').toLowerCase() === clusterFilter.toLowerCase();

      return matchesQuery && matchesRegion && matchesStatus && matchesCluster;
    });
  }, [opportunities, searchQuery, regionFilter, statusFilter, clusterFilter]);

  // Filtered Applications List
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const q = searchQuery.toLowerCase().trim();
      const matchesQuery = !q || (
        (app.id || '').toLowerCase().includes(q) ||
        (app.opportunityTitle || '').toLowerCase().includes(q) ||
        (app.opportunityId || '').toLowerCase().includes(q) ||
        (app.studentName || '').toLowerCase().includes(q) ||
        (app.studentEmail || '').toLowerCase().includes(q) ||
        (app.organizationName || '').toLowerCase().includes(q) ||
        (app.tenantId || '').toLowerCase().includes(q)
      );

      // Region filter
      const matchesRegion = regionFilter === 'all' || (app.tenantId || '').toLowerCase() === regionFilter.toLowerCase();

      // Status filter
      const matchesStatus = statusFilter === 'all' || (app.status || 'pending').toLowerCase() === statusFilter.toLowerCase();

      return matchesQuery && matchesRegion && matchesStatus;
    });
  }, [applications, searchQuery, regionFilter, statusFilter]);

  // Calculated Stats
  const oppStats = useMemo(() => {
    const total = opportunities.length;
    const open = opportunities.filter(o => o.status === 'open' || o.status === ('published' as any) || o.isOpen !== false).length;
    const closed = opportunities.filter(o => o.status === 'closed' || o.status === 'archived').length;
    const uniqueOrgs = new Set(opportunities.map(o => (o.organizationName || '').toLowerCase().trim())).size;
    return { total, open, closed, uniqueOrgs };
  }, [opportunities]);

  const appStats = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter(a => a.status === 'pending').length;
    const reviewed = applications.filter(a => a.status === 'reviewed' || a.interviewScheduled).length;
    const accepted = applications.filter(a => a.status === 'accepted').length;
    const rejected = applications.filter(a => a.status === 'rejected').length;
    return { total, pending, reviewed, accepted, rejected };
  }, [applications]);

  // Delete Opportunity Handler
  const handleDeleteOpp = async (oppId: string, oppTitle: string) => {
    showConfirm({
      title: 'Delete Opportunity posting?',
      message: `Are you sure you want to delete "${oppTitle}" (${oppId})? This action will remove the Firestore document and associated applications.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          await deleteOpportunity(oppId);
          showToast({
            title: 'Opportunity Deleted',
            message: `Posting "${oppTitle}" removed successfully.`,
            type: 'success'
          });
          if (selectedOpportunity?.id === oppId) {
            setSelectedOpportunity(null);
          }
        } catch (err: any) {
          showAlert({
            title: 'Delete Failed',
            message: err?.message || 'Error deleting opportunity document.',
            type: 'error'
          });
        }
      }
    });
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden p-6 space-y-6">
      
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
            <Briefcase className="w-6 h-6 text-[#0094c8]" />
            <span>Opportunities & Applications Audit Console</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1 uppercase font-extrabold tracking-wider">
            Review, inspect Firestore document IDs, track candidate pipelines, and verify employer postings across all portals.
          </p>
        </div>

        {/* View Switcher Subtabs */}
        <div className="bg-slate-100 p-1 rounded-2xl flex items-center border border-slate-200/80 shadow-2xs self-start md:self-auto">
          <button
            type="button"
            onClick={() => setActiveSubTab('opportunities')}
            className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider flex items-center space-x-2 rounded-xl transition-all cursor-pointer ${
              activeSubTab === 'opportunities'
                ? 'bg-white text-[#011d3c] shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Briefcase className={`w-3.5 h-3.5 ${activeSubTab === 'opportunities' ? 'text-[#0094c8]' : 'text-slate-400'}`} />
            <span>Opportunities ({opportunities.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('applications')}
            className={`px-4 py-2 text-[11px] font-black uppercase tracking-wider flex items-center space-x-2 rounded-xl transition-all cursor-pointer ${
              activeSubTab === 'applications'
                ? 'bg-white text-[#011d3c] shadow-xs border border-slate-200'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <FileText className={`w-3.5 h-3.5 ${activeSubTab === 'applications' ? 'text-[#0094c8]' : 'text-slate-400'}`} />
            <span>Applications ({applications.length})</span>
          </button>
        </div>
      </div>

      {/* Metrics Bar */}
      {activeSubTab === 'opportunities' ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-slate-400 font-mono">Total Opportunities</div>
              <div className="text-xl font-black text-slate-900 mt-0.5">{oppStats.total}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-200/60 text-slate-600 flex items-center justify-center shrink-0">
              <Briefcase className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-emerald-50/50 border border-emerald-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-emerald-700 font-mono">Active Postings</div>
              <div className="text-xl font-black text-emerald-900 mt-0.5">{oppStats.open}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-sky-50/50 border border-sky-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-sky-700 font-mono">Employer Hosts</div>
              <div className="text-xl font-black text-sky-900 mt-0.5">{oppStats.uniqueOrgs}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-amber-50/50 border border-amber-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-amber-700 font-mono">Closed / Archived</div>
              <div className="text-xl font-black text-amber-900 mt-0.5">{oppStats.closed}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5" />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-slate-400 font-mono">Total Applications</div>
              <div className="text-xl font-black text-slate-900 mt-0.5">{appStats.total}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-200/60 text-slate-600 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-amber-50/50 border border-amber-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-amber-700 font-mono">Pending Review</div>
              <div className="text-xl font-black text-amber-900 mt-0.5">{appStats.pending}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-sky-50/50 border border-sky-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-sky-700 font-mono">Interview / Reviewed</div>
              <div className="text-xl font-black text-sky-900 mt-0.5">{appStats.reviewed}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-emerald-50/50 border border-emerald-200/80 rounded-2xl p-4 flex items-center justify-between shadow-2xs">
            <div>
              <div className="text-[9.5px] font-black uppercase tracking-widest text-emerald-700 font-mono">Accepted / Hired</div>
              <div className="text-xl font-black text-emerald-900 mt-0.5">{appStats.accepted}</div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1">
          {/* Main Search Input */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={activeSubTab === 'opportunities' ? "Search opportunity title, org, ID, cluster..." : "Search student, email, opportunity, app ID..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-[#0094c8] outline-none placeholder-slate-400 shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Regional Portal Dropdown */}
          <div className="relative">
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:ring-2 focus:ring-[#0094c8] outline-none cursor-pointer shadow-2xs pr-8 appearance-none"
            >
              <option value="all">All Regional Portals</option>
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.id})</option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
          </div>

          {/* Status Filter Dropdown */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:ring-2 focus:ring-[#0094c8] outline-none cursor-pointer shadow-2xs pr-8 appearance-none"
            >
              <option value="all">All Statuses</option>
              {activeSubTab === 'opportunities' ? (
                <>
                  <option value="open">Open / Published</option>
                  <option value="closed">Closed</option>
                  <option value="archived">Archived</option>
                </>
              ) : (
                <>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed / Interview</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </>
              )}
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
          </div>

          {/* Career Cluster Filter (Opportunities view only) */}
          {activeSubTab === 'opportunities' && uniqueClusters.length > 0 && (
            <div className="relative">
              <select
                value={clusterFilter}
                onChange={(e) => setClusterFilter(e.target.value)}
                className="px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:ring-2 focus:ring-[#0094c8] outline-none cursor-pointer shadow-2xs pr-8 appearance-none max-w-[200px]"
              >
                <option value="all">All Career Clusters</option>
                {uniqueClusters.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
            </div>
          )}
        </div>

        {/* Counter and Clear Filters */}
        <div className="flex items-center space-x-3 text-xs font-mono text-slate-500 shrink-0">
          <span>
            Showing {activeSubTab === 'opportunities' ? filteredOpportunities.length : filteredApplications.length} records
          </span>
          {(searchQuery || regionFilter !== 'all' || statusFilter !== 'all' || clusterFilter !== 'all') && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setRegionFilter('all');
                setStatusFilter('all');
                setClusterFilter('all');
              }}
              className="text-[#0094c8] font-bold hover:underline cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {/* TABLE VIEW: OPPORTUNITIES */}
      {activeSubTab === 'opportunities' && (
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/90 border-b border-slate-200">
                <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 select-none">
                  <th className="py-3.5 px-4 w-44">Firestore Doc ID</th>
                  <th className="py-3.5 px-4">Opportunity Title & Cluster</th>
                  <th className="py-3.5 px-4">Employer Host</th>
                  <th className="py-3.5 px-4">Regional Portal</th>
                  <th className="py-3.5 px-4 text-center">Type / Duration</th>
                  <th className="py-3.5 px-4 text-center">Applicants</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredOpportunities.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-slate-400">
                      <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-2 animate-bounce" />
                      <p className="text-xs font-bold text-slate-600">No opportunities match your filter query.</p>
                    </td>
                  </tr>
                ) : (
                  filteredOpportunities.map((opp) => {
                    const appCount = applications.filter(a => a.opportunityId === opp.id).length;
                    const tenantMatch = tenants.find(t => t.id === opp.tenantId);

                    return (
                      <tr 
                        key={opp.id}
                        onClick={() => setSelectedOpportunity(opp)}
                        className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      >
                        {/* 1. Firestore Doc ID */}
                        <td className="py-3.5 px-4">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyId(opp.id, 'Opportunity ID');
                            }}
                            className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg text-[10.5px] font-mono font-bold text-slate-700 transition-all cursor-pointer group/id"
                            title="Click to copy Firestore Opportunity ID"
                          >
                            <Database className="w-3 h-3 text-[#0094c8]" />
                            <span className="truncate max-w-[100px]">{opp.id}</span>
                            {copiedId === opp.id ? (
                              <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                            ) : (
                              <Copy className="w-3 h-3 text-slate-400 group-hover/id:text-slate-700 shrink-0" />
                            )}
                          </button>
                        </td>

                        {/* 2. Title & Cluster */}
                        <td className="py-3.5 px-4">
                          <div>
                            <div className="font-extrabold text-slate-900 group-hover:text-[#0094c8] transition-colors uppercase tracking-tight text-xs">
                              {opp.title}
                            </div>
                            <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
                              {opp.careerCluster || opp.industry || 'General Pathway'}
                            </div>
                          </div>
                        </td>

                        {/* 3. Employer Host */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0 text-[10px] font-black border border-slate-200">
                              {opp.organizationName ? opp.organizationName.charAt(0).toUpperCase() : 'O'}
                            </div>
                            <span className="font-bold text-slate-800 text-xs truncate max-w-[180px]">
                              {opp.organizationName || 'Host Partner'}
                            </span>
                          </div>
                        </td>

                        {/* 4. Regional Portal */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[10px] font-mono font-bold text-slate-700 uppercase">
                            <Globe className="w-3 h-3 text-slate-400" />
                            <span>{tenantMatch?.name || opp.tenantId}</span>
                          </span>
                        </td>

                        {/* 5. Type & Duration */}
                        <td className="py-3.5 px-4 text-center font-mono text-[11px] text-slate-600">
                          <div className="font-bold uppercase text-[10px]">{opp.type || 'Internship'}</div>
                          <div className="text-[9.5px] text-slate-400">{opp.hoursRequired || opp.totalHours || 'N/A'} Hours</div>
                        </td>

                        {/* 6. Applicants Count */}
                        <td className="py-3.5 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black font-mono ${
                            appCount > 0 ? 'bg-sky-100 text-sky-800 border border-sky-200' : 'bg-slate-100 text-slate-400'
                          }`}>
                            {appCount} {appCount === 1 ? 'Applicant' : 'Applicants'}
                          </span>
                        </td>

                        {/* 7. Status */}
                        <td className="py-3.5 px-4 text-center">
                          <span className={`px-2.5 py-0.5 text-[9.5px] font-black uppercase rounded-md border font-mono ${
                            opp.status === 'open' || opp.status === ('published' as any) || opp.isOpen !== false
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200'
                          }`}>
                            {opp.status || 'open'}
                          </span>
                        </td>

                        {/* 8. Actions */}
                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOpportunity(opp);
                              }}
                              className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#0094c8] transition-colors cursor-pointer"
                              title="Inspect Opportunity Document"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOpp(opp.id, opp.title);
                              }}
                              className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                              title="Delete Opportunity"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TABLE VIEW: APPLICATIONS */}
      {activeSubTab === 'applications' && (
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/90 border-b border-slate-200">
                <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 select-none">
                  <th className="py-3.5 px-4 w-44">Firestore App ID</th>
                  <th className="py-3.5 px-4">Student Applicant</th>
                  <th className="py-3.5 px-4">Opportunity & Host</th>
                  <th className="py-3.5 px-4">Regional Portal</th>
                  <th className="py-3.5 px-4 text-center">Submitted Date</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400">
                      <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2 animate-bounce" />
                      <p className="text-xs font-bold text-slate-600">No applications match your filter query.</p>
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app) => {
                    const tenantMatch = tenants.find(t => t.id === app.tenantId);
                    const studentUser = users.find(u => u.uid === app.studentId || u.email === app.studentEmail);

                    return (
                      <tr 
                        key={app.id}
                        onClick={() => setSelectedApplication(app)}
                        className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                      >
                        {/* 1. Firestore App ID */}
                        <td className="py-3.5 px-4">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyId(app.id, 'Application ID');
                            }}
                            className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg text-[10.5px] font-mono font-bold text-slate-700 transition-all cursor-pointer group/id"
                            title="Click to copy Firestore Application ID"
                          >
                            <Database className="w-3 h-3 text-[#0094c8]" />
                            <span className="truncate max-w-[100px]">{app.id}</span>
                            {copiedId === app.id ? (
                              <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                            ) : (
                              <Copy className="w-3 h-3 text-slate-400 group-hover/id:text-slate-700 shrink-0" />
                            )}
                          </button>
                        </td>

                        {/* 2. Student Applicant */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200 shrink-0 shadow-2xs">
                              <StudentAvatar 
                                studentId={app.studentId}
                                studentName={app.studentName}
                                studentEmail={app.studentEmail}
                                profile={studentUser}
                              />
                            </div>
                            <div>
                              <div className="font-extrabold text-slate-900 group-hover:text-[#0094c8] transition-colors uppercase tracking-tight text-xs">
                                {formatLastNameFirst(app.studentName, studentUser)}
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono">
                                {app.studentEmail}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* 3. Opportunity & Host */}
                        <td className="py-3.5 px-4">
                          <div>
                            <div className="font-bold text-slate-800 text-xs uppercase tracking-tight">
                              {app.opportunityTitle}
                            </div>
                            <div className="text-[10px] text-[#0094c8] font-bold uppercase tracking-wider mt-0.5">
                              {app.organizationName}
                            </div>
                          </div>
                        </td>

                        {/* 4. Regional Portal */}
                        <td className="py-3.5 px-4">
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-md text-[10px] font-mono font-bold text-slate-700 uppercase">
                            <Globe className="w-3 h-3 text-slate-400" />
                            <span>{tenantMatch?.name || app.tenantId}</span>
                          </span>
                        </td>

                        {/* 5. Submitted Date */}
                        <td className="py-3.5 px-4 text-center font-mono text-[10.5px] text-slate-600">
                          {formatDate(app.appliedAt)}
                        </td>

                        {/* 6. Status */}
                        <td className="py-3.5 px-4 text-center">
                          <span className={`px-2.5 py-0.5 text-[9.5px] font-black uppercase rounded-md border font-mono ${
                            app.status === 'accepted' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                            app.status === 'rejected' ? 'bg-rose-100 text-rose-800 border-rose-200' :
                            app.status === 'reviewed' ? 'bg-sky-100 text-sky-800 border-sky-200' :
                            'bg-amber-100 text-amber-800 border-amber-200'
                          }`}>
                            {app.status}
                          </span>
                        </td>

                        {/* 7. Actions */}
                        <td className="py-3.5 px-4 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedApplication(app);
                            }}
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-[#0094c8] transition-colors cursor-pointer"
                            title="Inspect Application Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POPOUT MODAL: OPPORTUNITY DETAILS & FIRESTORE ID */}
      {selectedOpportunity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200 flex flex-col">
            
            {/* Header */}
            <div className="bg-[#011d3c] p-6 text-white flex items-center justify-between">
              <div>
                <span className="px-2.5 py-0.5 bg-[#0094c8] text-white text-[9.5px] font-black uppercase rounded font-mono tracking-widest">
                  Opportunity Document
                </span>
                <h3 className="text-xl font-black uppercase tracking-tight mt-1.5">{selectedOpportunity.title}</h3>
                <p className="text-xs text-white/60 font-medium mt-0.5">{selectedOpportunity.organizationName}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedOpportunity(null)}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Firestore ID Highlight Bar */}
            <div className="bg-slate-900 px-6 py-3 border-b border-slate-800 flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2 text-white/80 font-mono">
                <Database className="w-4 h-4 text-[#0094c8]" />
                <span className="text-white/40 uppercase font-bold text-[10px]">Firestore Document ID:</span>
                <span className="font-bold text-sky-400 select-all">{selectedOpportunity.id}</span>
              </div>
              <button
                type="button"
                onClick={() => handleCopyId(selectedOpportunity.id, 'Firestore Opportunity ID')}
                className="px-3 py-1 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all flex items-center space-x-1 cursor-pointer"
              >
                <Copy className="w-3 h-3" />
                <span>Copy ID</span>
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-700 flex-1">
              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-200/80 font-mono">
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Regional Portal</span>
                  <span className="font-bold text-slate-800">{selectedOpportunity.tenantId}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Career Cluster</span>
                  <span className="font-bold text-slate-800">{selectedOpportunity.careerCluster || selectedOpportunity.industry || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Type / Hours</span>
                  <span className="font-bold text-slate-800">{selectedOpportunity.type || 'Internship'} • {selectedOpportunity.hoursRequired || selectedOpportunity.totalHours || 'N/A'} hrs</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Status</span>
                  <span className="font-bold text-emerald-600 uppercase">{selectedOpportunity.status || 'open'}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Created Date</span>
                  <span className="font-bold text-slate-800">{formatDate(selectedOpportunity.createdAt)}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Contact Email</span>
                  <span className="font-bold text-slate-800 truncate block">{selectedOpportunity.externalContactEmail || 'N/A'}</span>
                </div>
              </div>

              {/* Worksite Address */}
              {selectedOpportunity.address && (
                <div className="space-y-1">
                  <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">Worksite Address</h4>
                  <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-xl border border-slate-200 text-slate-800 font-semibold">
                    <MapPin className="w-4 h-4 text-[#0094c8] shrink-0" />
                    <span>{selectedOpportunity.address}</span>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="space-y-2">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">Position Description</h4>
                <div className="p-4 bg-slate-50/60 rounded-2xl border border-slate-200/80 leading-relaxed text-slate-700 whitespace-pre-wrap">
                  {selectedOpportunity.description || 'No description provided.'}
                </div>
              </div>

              {/* Requirements & Skills */}
              {(selectedOpportunity.requirements?.length > 0 || selectedOpportunity.qualifications || selectedOpportunity.skills) && (
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">Requirements & Skills</h4>
                  {selectedOpportunity.qualifications && (
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <span className="text-[10px] font-bold text-[#0094c8] uppercase block mb-1">Qualifications</span>
                      <p>{selectedOpportunity.qualifications}</p>
                    </div>
                  )}
                  {selectedOpportunity.skills && (
                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <span className="text-[10px] font-bold text-[#0094c8] uppercase block mb-1">Skills</span>
                      <p>{selectedOpportunity.skills}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Associated Applicants in Context */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">
                  Submitted Candidate Applications ({applications.filter(a => a.opportunityId === selectedOpportunity.id).length})
                </h4>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                  {applications.filter(a => a.opportunityId === selectedOpportunity.id).length === 0 ? (
                    <div className="p-4 text-center text-slate-400 italic">No student applications submitted for this posting yet.</div>
                  ) : (
                    applications.filter(a => a.opportunityId === selectedOpportunity.id).map(app => (
                      <div key={app.id} className="p-3 flex items-center justify-between bg-slate-50/50 hover:bg-slate-50">
                        <div>
                          <div className="font-bold text-slate-900 uppercase">{app.studentName}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{app.studentEmail} • ID: {app.id}</div>
                        </div>
                        <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded font-mono ${
                          app.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                        }`}>
                          {app.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleDeleteOpp(selectedOpportunity.id, selectedOpportunity.title)}
                className="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl text-xs font-bold uppercase transition-all flex items-center space-x-1.5 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Posting</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedOpportunity(null)}
                className="px-5 py-2 bg-[#011d3c] hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POPOUT MODAL: APPLICATION DETAILS & FIRESTORE ID */}
      {selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200 flex flex-col">
            
            {/* Header */}
            <div className="bg-[#011d3c] p-6 text-white flex items-center justify-between">
              <div>
                <span className="px-2.5 py-0.5 bg-[#0094c8] text-white text-[9.5px] font-black uppercase rounded font-mono tracking-widest">
                  Application Document
                </span>
                <h3 className="text-xl font-black uppercase tracking-tight mt-1.5">{selectedApplication.studentName}</h3>
                <p className="text-xs text-white/60 font-medium mt-0.5">{selectedApplication.opportunityTitle} • {selectedApplication.organizationName}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedApplication(null)}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Firestore ID Highlight Bar */}
            <div className="bg-slate-900 px-6 py-3 border-b border-slate-800 flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2 text-white/80 font-mono">
                <Database className="w-4 h-4 text-[#0094c8]" />
                <span className="text-white/40 uppercase font-bold text-[10px]">Firestore Application ID:</span>
                <span className="font-bold text-sky-400 select-all">{selectedApplication.id}</span>
              </div>
              <button
                type="button"
                onClick={() => handleCopyId(selectedApplication.id, 'Firestore Application ID')}
                className="px-3 py-1 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all flex items-center space-x-1 cursor-pointer"
              >
                <Copy className="w-3 h-3" />
                <span>Copy ID</span>
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-700 flex-1">
              {/* Quick Metadata Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-200/80 font-mono">
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Regional Portal</span>
                  <span className="font-bold text-slate-800">{selectedApplication.tenantId}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Status</span>
                  <span className="font-bold text-emerald-600 uppercase">{selectedApplication.status}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Applied Date</span>
                  <span className="font-bold text-slate-800">{formatDate(selectedApplication.appliedAt)}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Student Email</span>
                  <span className="font-bold text-slate-800 truncate block">{selectedApplication.studentEmail}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Student ID</span>
                  <span className="font-bold text-[#0094c8] truncate block select-all">{selectedApplication.studentId}</span>
                </div>
                <div>
                  <span className="text-[9.5px] font-black text-slate-400 uppercase block">Opportunity ID</span>
                  <span className="font-bold text-[#0094c8] truncate block select-all">{selectedApplication.opportunityId}</span>
                </div>
              </div>

              {/* Cover Letter / Supplemental Submission */}
              {selectedApplication.coverLetter && (
                <div className="space-y-2">
                  <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">Supplemental Cover Letter / Submission</h4>
                  <div className="p-4 bg-slate-50/60 rounded-2xl border border-slate-200/80 leading-relaxed text-slate-700 whitespace-pre-wrap">
                    {selectedApplication.coverLetter}
                  </div>
                </div>
              )}

              {/* Related Opportunity Details in Context */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">Position Context</h4>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                  <div className="font-extrabold text-slate-900 uppercase text-xs">{selectedApplication.opportunityTitle}</div>
                  <div className="text-[11px] font-bold text-[#0094c8] uppercase">{selectedApplication.organizationName}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setSelectedApplication(null)}
                className="px-5 py-2 bg-[#011d3c] hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
